import React, {useEffect, useRef, useState} from 'react';
// Components
import Sidebar from "./Sidebar";
import LandingSection from "./LandingSection";
import OurService from "./OurService";
import OurProject from "./OurProject";
import AboutUs from "./AboutUs";
import ContactUs from "./ContactUs";
// CSS
import "../../styles/tailwind-theme-three.css";
import "../../styles/custom-theme-three.css";
import Preloader from "./custom-components/Preloader";
import ThemeThreePreloader from "./custom-components/ThemeThreePreLoader";

const ThemeThree = () => {
    const [themeThreeTransition, setThemeThreeTransition] = useState<boolean>(false);
    const colors = [
        { id: "home", color: "#9CD6F6" },
        { id: "our-services", color: "#F69C9C" },
        { id: "our-projects", color: "#598DAA" },
        { id: "about-us", color: "#79C4C8" },
        { id: "contact-us", color: "#799DC8" },
    ];
    const transitionTimeouts = useRef<number[]>([]);
    const currentLayer = useRef<VisibleLayerProps | null>(null);
    const pendingLayer = useRef<VisibleLayerProps | null>(null);

    const getLayerFromSection = (section: string): VisibleLayerProps => ({
        home: section === "home" || section === "",
        services: section === "our-services",
        projects: section === "our-projects",
        about_us: section === "about-us",
        contact_us: section === "contact-us",
    });

    const getSectionFromLayer = (layer: VisibleLayerProps) => {
        if (layer.services) return "our-services";
        if (layer.projects) return "our-projects";
        if (layer.about_us) return "about-us";
        if (layer.contact_us) return "contact-us";
        return "home";
    };

    const getInitialLayer = () => {
        const hashValue = window.location.hash;
        const extractedTag = hashValue && hashValue.startsWith("#") ? hashValue.slice(1) : "home";
        return getLayerFromSection(colors.some((item) => item.id === extractedTag) ? extractedTag : "home");
    };

    const getBackgroundColor = (layer: VisibleLayerProps) => {
        const section = getSectionFromLayer(layer);
        return colors.find((item) => item.id === section)?.color || "#9CD6F6";
    };

    const isSameLayer = (first: VisibleLayerProps | null, second: VisibleLayerProps) => {
        return Boolean(first)
            && first?.home === second.home
            && first?.services === second.services
            && first?.projects === second.projects
            && first?.about_us === second.about_us
            && first?.contact_us === second.contact_us;
    };

    const initialLayer = getInitialLayer();
    const [visibleLayer, setVisibleLayer] = useState<VisibleLayerProps>(initialLayer);
    const [backgroundColor, setBackgroundColor] = useState(getBackgroundColor(initialLayer));
    const [transitionKey, setTransitionKey] = useState(0);

    const clearTransitionTimeouts = () => {
        transitionTimeouts.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
        transitionTimeouts.current = [];
    };

    const applyLayer = (layer: VisibleLayerProps) => {
        currentLayer.current = layer;
        pendingLayer.current = null;
        setVisibleLayer(layer);
        setBackgroundColor(getBackgroundColor(layer));
    };

    const requestLayerChange = (layer: VisibleLayerProps, updateHash = true) => {
        const comparisonLayer = pendingLayer.current || currentLayer.current;
        if (isSameLayer(comparisonLayer, layer)) return;

        clearTransitionTimeouts();
        pendingLayer.current = layer;

        if (updateHash) {
            const hash = `#${getSectionFromLayer(layer)}`;
            if (window.location.hash !== hash) {
                window.history.replaceState(null, "", hash);
            }
        }

        setTransitionKey((value) => value + 1);
        transitionTimeouts.current.push(window.setTimeout(() => {
            applyLayer(layer);
        }, 1500));
    };

    useEffect(() => {
        currentLayer.current = visibleLayer;
    }, [visibleLayer]);

    useEffect(() => {
        // Function to handle hash change
        const handleHashChange = () => {
            const hashValue = window.location.hash;

            // Check if the hash is not empty and starts with '#'
            if (hashValue && hashValue.startsWith('#')) {
                // Extract the hashtag from the hash (excluding '#')
                const extractedTag = hashValue.slice(1);
                const nextLayer = getLayerFromSection(colors.some((item) => item.id === extractedTag) ? extractedTag : "home");
                requestLayerChange(nextLayer, false);
            } else {
                requestLayerChange(getLayerFromSection("home"), false);
            }
        };
        // Event listener for hashchange
        window.addEventListener("hashchange", handleHashChange);
        // Clean up the event listener on component unmount
        return () => {
            clearTransitionTimeouts();
            window.removeEventListener("hashchange", handleHashChange);
        };
        // The hash listener is mounted once and reads the active target through refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="w-full h-screen">
            <Preloader transitionKey={transitionKey} />
            <ThemeThreePreloader animation={themeThreeTransition} />
            <style dangerouslySetInnerHTML={{__html: `body { background: ${backgroundColor}; overflow-x: hidden; }`}}/>
            {/* Sidebar */}
            <Sidebar layer={visibleLayer} setLayer={requestLayerChange}/>
            {/* Landing Section */}
            <LandingSection layer={visibleLayer} setLayer={requestLayerChange} setThemeOne={setThemeThreeTransition}/>
            {/* Our Services */}
            <OurService layer={visibleLayer}/>
            {/* Our Projects */}
            <OurProject layer={visibleLayer}/>
            {/* About Us */}
            <AboutUs layer={visibleLayer}/>
            {/* Contact Us */}
            <ContactUs layer={visibleLayer}/>
        </div>
    );
};

interface VisibleLayerProps {
    home: boolean;
    services: boolean;
    projects: boolean;
    about_us: boolean;
    contact_us: boolean;
}

export default ThemeThree;
